import { useCallback } from 'react'
import { useCallStore } from '../stores/callStore'
import { usePresenceStore } from '../stores/presenceStore'
import { useLineChatStore } from '../stores/lineChatStore'
import type { WsMessage } from '../types'

interface User {
  sub?: string
  organizationId?: string
}

/**
 * WebSocketメッセージハンドリングのロジックを管理するカスタムフック
 * Layout.jsxから分離して責務を明確化
 */
export function useWebSocketHandler(user: User | null) {
  // Layout再レンダリング防止: useCallStore.getState()をコールバック内で使用
  const { setActiveOperators } = usePresenceStore()

  const handleWsMessage = useCallback((message: WsMessage) => {
    const store = useCallStore.getState()

    // eventType ベースのメッセージ
    if ('eventType' in message) {
      // 着信キュー追加
      if (message.eventType === 'call.queued') {
        console.log('[useWebSocketHandler] WebSocketで着信通知受信:', message.data)
        store.handleIncomingQueued(message.data)

        // Phase 7: callHistoryIdがあれば保存（着信時のメモ保存用）
        if (message.data?.callHistoryId) {
          store.setCurrentCallHistoryId(message.data.callHistoryId)
          console.log('[useWebSocketHandler] callHistoryIdを保存:', message.data.callHistoryId)
        }

        // デスクトップ通知（画面が非フォーカス時のみ）
        if (document.visibilityState !== 'visible' && 'Notification' in window && Notification.permission === 'granted') {
          new Notification('新しい着信', {
            body: `${message.data.from || '不明'}から着信があります`,
            icon: '/phone-icon.png',
            tag: 'incoming-call',
          })
        }
      }

      // 着信応答/削除
      if (message.eventType === 'call.answered' || message.eventType === 'call.removed') {
        const callId = message.data?.callId
        const answeredBy = message.eventType === 'call.answered' ? message.data?.answeredBy : undefined

        console.log('[useWebSocketHandler] call.answered/removed イベント受信:', {
          eventType: message.eventType,
          callId,
          answeredBy,
          currentCallId: store.currentCallId,
          currentUserId: user?.sub,
          callStatus: store.callStatus,
        })

        if (callId) {
          // 自分が応答した call.answered は完全に無視する
          // device.on('incoming') → call.accept() → startCall で処理されるため、
          // ここでキュー削除やリセットを行うと device.on('incoming') 到着前にモーダルが消える
          // 自分の応答でも answeredVia='mobile' (同一アカウントのモバイル端末での応答) は
          // このブラウザの応答ではないため、キュー表示を消す側に倒す
          const isSelfAnswer = message.eventType === 'call.answered' && answeredBy === user?.sub && message.data?.answeredVia !== 'mobile'
          if (isSelfAnswer) {
            console.log('[useWebSocketHandler] 自分が応答した通話のため無視:', callId)
          } else {
            // 他のオペレータの応答 or call.removed → handleCallGone で状態遷移を一元管理
            store.handleCallGone(callId)
          }
        }
      }

      // Presence更新
      if (message.eventType === 'presence.update') {
        if (message.organizationId && user?.organizationId === message.organizationId) {
          setActiveOperators(message.items || [])
        }
      }

      // 保留状態更新
      if (message.eventType === 'hold.update') {
        if (message.organizationId && user?.organizationId === message.organizationId) {
          store.setHeldCalls(message.items || [])
        }
      }

      // LINE メッセージ受信
      if (message.eventType === 'line.message') {
        const lineStore = useLineChatStore.getState()
        const d = message.data
        lineStore.addMessage({
          organizationId: '',
          messageId: d.messageId,
          lineUserId: d.lineUserId,
          direction: d.direction,
          type: d.type,
          text: d.text,
          stickerPackageId: d.stickerPackageId,
          stickerId: d.stickerId,
          stickerResourceType: d.stickerResourceType,
          mediaUrl: d.mediaUrl,
          thumbnailUrl: d.thumbnailUrl,
          fileName: d.fileName,
          fileSize: d.fileSize,
          latitude: d.latitude,
          longitude: d.longitude,
          address: d.address,
          duration: d.duration,
          createdAt: d.createdAt,
        })
        const previewMap: Record<string, string> = {
          sticker: '[スタンプ]',
          image: '[画像]',
          video: '[動画]',
          audio: '[音声]',
          location: '[位置情報]',
          file: d.fileName ? `[ファイル] ${d.fileName}` : '[ファイル]',
        }
        const previewText = previewMap[d.type] ?? (d.text ?? '')
        lineStore.updateConversationLastMessage(d.lineUserId, previewText, d.createdAt)

        if (d.direction === 'incoming') {
          lineStore.incrementUnread(d.lineUserId)

          // デスクトップ通知（該当会話を閲覧中でない場合のみ）
          const isViewingConversation = lineStore.selectedLineUserId === d.lineUserId && document.visibilityState === 'visible'
          if (!isViewingConversation && 'Notification' in window && Notification.permission === 'granted') {
            const conv = lineStore.conversations.find(c => c.lineUserId === d.lineUserId)
            const senderName = conv?.displayName ?? 'LINE ユーザー'
            const bodyText = d.type === 'text'
              ? `${(d.text ?? '').substring(0, 80)}`
              : (previewText)
            new Notification(`${senderName}`, {
              body: bodyText,
              icon: conv?.pictureUrl ?? '/phone-icon.png',
              tag: `line-${d.lineUserId}`,
            })
          }
        }
      }

      // LINE フォロー（新規友だち追加）
      if (message.eventType === 'line.follow') {
        const lineStore = useLineChatStore.getState()
        lineStore.addConversation({
          organizationId: '',
          lineUserId: message.data.lineUserId,
          displayName: message.data.displayName ?? null,
          pictureUrl: message.data.pictureUrl ?? null,
          status: 'active',
          unreadCount: 0,
          lastMessage: null,
          lastMessageAt: message.data.createdAt ?? new Date().toISOString(),
          createdAt: message.data.createdAt ?? new Date().toISOString(),
          updatedAt: message.data.createdAt ?? new Date().toISOString(),
        })
      }

      // LINE アンフォロー（友だち削除/ブロック）
      if (message.eventType === 'line.unfollow') {
        const lineStore = useLineChatStore.getState()
        lineStore.setConversationInactive(message.data.lineUserId)
      }

      // 保留削除通知（他のオペレータが保留解除した場合）
      if (message.eventType === 'hold-call.removed') {
        const { holdQueueName: removedHoldQueueName } = message.data || {}
        if (removedHoldQueueName) {
          console.log('[useWebSocketHandler] 保留削除通知受信:', removedHoldQueueName)
          const latestStore = useCallStore.getState()
          latestStore.removeHeldCall(removedHoldQueueName)

          if (latestStore.holdQueueName === removedHoldQueueName) {
            console.log('[useWebSocketHandler] 自分の保留が他のオペレータに解除されました')
            latestStore.clearHoldQueueName()
          }
        }
      }
    }

    // type ベースのメッセージ（内線通話）
    if ('type' in message) {
      // 内線通話: 着信
      if (message.type === 'internal_call_incoming') {
        if (message.data?.targetId === user?.sub) {
          console.log('[useWebSocketHandler] 内線着信受信:', message.data)
          const existing = useCallStore.getState().incomingInternalCall
          store.setIncomingInternalCall({
            internalCallId: message.data.internalCallId,
            callerId: message.data.callerId,
            callerName: message.data.callerName,
            twilioCall: existing?.twilioCall || null,
          })
        }
      }

      // 内線通話: 応答
      if (message.type === 'internal_call_answered') {
        if (message.data?.callerId === user?.sub || message.data?.targetId === user?.sub) {
          console.log('[useWebSocketHandler] 内線通話が応答されました:', message.data)
          store.setInternalCallStatus('connected')
          store.clearIncomingInternalCall()
        }
      }

      // 内線通話: 拒否
      if (message.type === 'internal_call_rejected') {
        if (message.data?.callerId === user?.sub) {
          console.log('[useWebSocketHandler] 内線通話が拒否されました:', message.data)
          store.setInternalCallStatus('idle')
          // 転送着信や外線通話が進行中でなければ通話状態をクリア
          // currentCallIdが設定済み = 転送着信のaccept待ちまたはキュー応答中の可能性があるため、
          // それらが設定されている場合はクリアしない
          const latestState = useCallStore.getState()
          if (!latestState.incomingCall && !latestState.activeCall && !latestState.currentCallId) {
            store.setCallStatus('idle')
            store.setCallerNumber(null)
          } else {
            console.log('[useWebSocketHandler] 内線拒否: 通話状態クリアをスキップ（進行中の通話あり）:', {
              hasIncomingCall: !!latestState.incomingCall,
              hasActiveCall: !!latestState.activeCall,
              currentCallId: latestState.currentCallId,
              callStatus: latestState.callStatus,
            })
          }
          alert('内線通話が拒否されました')
        }
        if (message.data?.targetId === user?.sub) {
          store.clearIncomingInternalCall()
        }
      }

      // 内線通話: 終了
      if (message.type === 'internal_call_ended') {
        if (message.data?.callerId === user?.sub || message.data?.targetId === user?.sub) {
          console.log('[useWebSocketHandler] 内線通話が終了しました:', message.data)
          store.setInternalCallStatus('idle')
          store.clearIncomingInternalCall()
          // 転送着信や外線通話が進行中でなければ通話状態をクリア
          // currentCallId/callerNumberが設定済み = 転送着信のaccept待ちの可能性があるため、
          // それらが設定されている場合はクリアしない
          const latestState = useCallStore.getState()
          if (!latestState.incomingCall && !latestState.activeCall && !latestState.currentCallId) {
            store.setCallStatus('idle')
            store.setCallerNumber(null)
          }
        }
      }

      // 転送割り当て: 保留中の通話が自分に転送された
      if (message.type === 'transfer_call_assigned') {
        if (message.data?.targetUserId === user?.sub) {
          console.log('[useWebSocketHandler] 転送割り当て受信:', message.data)
          store.setPendingTransfer({
            holdQueueName: message.data.holdQueueName,
            originalCallId: message.data.originalCallId,
            callerNumber: message.data.callerNumber,
            callHistoryId: message.data.callHistoryId,
          })
        }
      }
    }
  }, [user?.organizationId, user?.sub, setActiveOperators])

  return handleWsMessage
}
